/**
 * Ticket #328 / #357: PI/shipping sin anticipo de cliente cobrado.
 *
 * El pendiente sale de `total_remaining_advance`, que recalcula el servidor en cada guardado
 * y en los hooks de cobro. Sin ese campo se usa anticipo esperado − cobrado **de adelanto**
 * (appliedto), no `total_collected` (todos los cobros); y si el cobrado de adelanto supera el
 * cobrado total, se descarta por venir heredado de una duplicación.
 */

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function roundMoney(value) {
  return Math.round(value * 100) / 100;
}

function hasOwnAdvanceField(record, key) {
  return (
    record &&
    Object.prototype.hasOwnProperty.call(record, key) &&
    record[key] !== null &&
    record[key] !== undefined &&
    record[key] !== ""
  );
}

function expectedMinusCollectedField(record, collectedKey) {
  if (!record || typeof record !== "object") return null;
  if (
    !hasOwnAdvanceField(record, "expected_advance") ||
    !hasOwnAdvanceField(record, collectedKey)
  ) {
    return null;
  }
  const expected = toFiniteNumber(record.expected_advance);
  const collected = toFiniteNumber(record[collectedKey]);
  if (expected == null || collected == null) return null;
  return roundMoney(Math.max(0, expected - collected));
}

/** Pendiente de adelanto (expected − cobrado a cuenta de anticipo). */
function resolveExpectedMinusAdvanceCollected(record) {
  return expectedMinusCollectedField(record, "total_collected_advance");
}

/**
 * El adelanto cobrado es parte del cobrado total: si lo supera, el campo no es fiable
 * (valor heredado al duplicar) y el pendiente se toma del que calcula el servidor.
 */
function advanceCollectedIsConsistent(record) {
  if (
    !hasOwnAdvanceField(record, "total_collected_advance") ||
    !hasOwnAdvanceField(record, "total_collected")
  ) {
    return true;
  }
  const advanceCollected = toFiniteNumber(record.total_collected_advance);
  const collected = toFiniteNumber(record.total_collected);
  if (advanceCollected == null || collected == null) return true;
  return advanceCollected <= collected + 0.01;
}

/** Fallback: expected − total_collected (todos los cobros; puede estar sucio al duplicar). */
function resolveExpectedMinusCollected(record) {
  return expectedMinusCollectedField(record, "total_collected");
}

/** Pendiente cargado en el propio registro. */
function resolveOwnPendingAdvanceAmount(record) {
  if (!record || typeof record !== "object") return null;
  // Lo calcula el servidor en cada guardado y en los hooks de cobro: fuente de verdad.
  if (hasOwnAdvanceField(record, "total_remaining_advance")) {
    return toFiniteNumber(record.total_remaining_advance);
  }
  if (advanceCollectedIsConsistent(record)) {
    const fromAdvanceCollected = resolveExpectedMinusAdvanceCollected(record);
    if (fromAdvanceCollected != null) return fromAdvanceCollected;
  }
  if (hasOwnAdvanceField(record, "remaining_advance")) {
    return toFiniteNumber(record.remaining_advance);
  }
  return resolveExpectedMinusCollected(record);
}

function shippingsOf(record) {
  return record && Array.isArray(record.shippings) ? record.shippings : [];
}

/** Proforma padre poblada en el embarque. */
function parentProformOf(record) {
  const parent = record && record.custom_proform;
  return parent && typeof parent === "object" ? parent : null;
}

function advanceShare(pendingAmount, shippingsCount) {
  if (pendingAmount == null) return null;
  const count = shippingsCount > 0 ? shippingsCount : 1;
  return roundMoney(pendingAmount / count);
}

/**
 * Anticipo pendiente del registro. Prioriza sus propios montos; en embarques sin
 * ellos usa la parte que le toca del anticipo de la proforma padre.
 * @param {Object} record
 * @returns {number|null}
 */
export function resolvePendingAdvanceAmount(record) {
  const own = resolveOwnPendingAdvanceAmount(record);
  if (own != null) return own;

  const parent = parentProformOf(record);
  if (parent) {
    const parentPending = resolveOwnPendingAdvanceAmount(parent);
    const parentShippings = shippingsOf(parent).length;
    if (parentPending != null && parentShippings > 0) {
      return advanceShare(parentPending, parentShippings);
    }
  }
  return null;
}

export function hasMissingAssociatedPrepayment(record) {
  const pending = resolvePendingAdvanceAmount(record);
  return pending != null && pending > 0;
}

export function isProformOrShippingModelName(modelName) {
  const m = String(modelName || "")
    .trim()
    .toLowerCase();
  return m === "proform" || m === "proforma" || m === "shipping";
}

function formatAdvanceAmount(amount) {
  const n = toFiniteNumber(amount);
  if (n == null) return "";
  try {
    return new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (e) {
    return String(n);
  }
}

function formatMoneyTooltip(amount) {
  const formatted = formatAdvanceAmount(amount);
  return formatted ? `$${formatted}` : "";
}

function pendingFromOverrides(pendingByShippingId, shippingId) {
  if (!pendingByShippingId || !shippingId) return null;
  const raw = pendingByShippingId[shippingId];
  return toFiniteNumber(raw);
}

/**
 * Embarques con anticipo pendiente de la proforma, con el monto de cada uno.
 * @param {Object} record Proforma con shippings poblados
 * @param {Object} [pendingByShippingId] Monto por embarque (desglose de Finanzas)
 * @returns {Array<{id: string, label: string, amount: number}>}
 */
export function listPendingPrepaymentShippings(record, pendingByShippingId) {
  const shippings = shippingsOf(record);
  if (shippings.length === 0) return [];

  const proformPending = resolveOwnPendingAdvanceAmount(record);
  const fallbackShare = advanceShare(proformPending, shippings.length);

  const entries = [];
  for (const shipping of shippings) {
    if (!shipping || typeof shipping !== "object") continue;
    const label = shipping.name || shipping.label || "";
    if (!label) continue;
    const id = shipping._id ? String(shipping._id) : "";

    let amount = pendingFromOverrides(pendingByShippingId, id);
    if (amount == null) amount = resolveOwnPendingAdvanceAmount(shipping);
    if (amount == null) amount = fallbackShare;
    if (amount == null || amount <= 0) continue;

    entries.push({ id, label: String(label), amount });
  }
  return entries;
}

function formatShippingLine(entry) {
  const money = formatMoneyTooltip(entry.amount);
  return money ? `${entry.label} — ${money}` : entry.label;
}

/**
 * Tooltip de un embarque concreto dentro de la proforma de la fila.
 * @param {Object} parentRecord Proforma con shippings
 * @param {string} shippingId
 * @param {string} shippingLabel
 * @returns {string} Vacío si no se puede resolver el monto de ese embarque.
 */
export function buildMissingPrepaymentTooltipForShipping(
  parentRecord,
  shippingId,
  shippingLabel
) {
  const wantedId = shippingId ? String(shippingId) : "";
  const wantedLabel = shippingLabel ? String(shippingLabel) : "";
  const entry = listPendingPrepaymentShippings(parentRecord).find(
    (e) =>
      (wantedId && e.id === wantedId) || (wantedLabel && e.label === wantedLabel)
  );
  return entry ? formatShippingLine(entry) : "";
}

/**
 * Tooltip del warning: una línea `NOMBRE — $MONTO` por embarque pendiente; si no
 * hay embarques poblados, solo el monto del registro.
 * @param {Object} record
 * @param {string} baseLabel
 * @param {Object} [pendingByShippingId] Monto por embarque (desglose de Finanzas)
 * @returns {string}
 */
export function buildMissingPrepaymentTooltip(
  record,
  baseLabel,
  pendingByShippingId
) {
  const lines = listPendingPrepaymentShippings(
    record,
    pendingByShippingId
  ).map(formatShippingLine);
  if (lines.length > 0) {
    return lines.join("\n");
  }

  const pending = resolvePendingAdvanceAmount(record);
  if (pending != null && pending > 0) {
    return formatMoneyTooltip(pending);
  }

  return baseLabel || "Sin prepayment asociado";
}
